import { forwardRef, useImperativeHandle, useRef } from 'react';
import Video from './video';
import Quiz from './quiz';
import Article from './article';
import ProjectItem from '../app/pages/teacher/components/ProjectItem';
import type { ArticleRef } from "@/types/article.types";
import type { QuizRef } from "@/types/quiz.types";
import type { VideoRef } from "@/types/video.types";
import type { ItemContainerProps, ItemContainerRef } from '@/types/item-container.types';
import FeedbackForm from '@/app/pages/student/components/FeedbackForm';
import ReflectionItemPanel, {type ReflectionItemPanelRef} from '@/components/peer-reviews/ReflectionItemPanel';
import CaseStudyItemPanel, {type CaseStudyItemPanelRef} from '@/components/case-studies/CaseStudyItemPanel';
import { useSubmitFeedback } from '@/hooks/hooks';

export interface ISubmitFeedbackBody {
  details: Record<string, any>;
  courseId: string;
  courseVersionId: string;
  // isSkipped?: boolean;
  cohortId?: string;
}
const ItemContainer = forwardRef<ItemContainerRef, ItemContainerProps>(({ item, nextItem, doGesture, onNext, onPrevVideo, isProgressUpdating, isNavigatingToPrev, readyToDetect, attemptId, anomalies, setQuizPassed, setAttemptId, rewindVid, pauseVid, pauseSignal, awayPaused, displayNextLesson, keyboardLockEnabled, setIsQuizSkipped, linearProgressionEnabled, seekForwardEnabled, courseId, versionId, completedItemIdsRef, cohortId, cohortName, previousItem, pendingStudentQuestionContext, clearPendingStudentQuestionContext, focusMode }, ref) => {
  const articleRef = useRef<ArticleRef>(null);
  const quizRef = useRef<QuizRef>(null);
  const reflectionRef = useRef<ReflectionItemPanelRef>(null);
  const caseStudyRef = useRef<CaseStudyItemPanelRef>(null);
  const videoRef = useRef<VideoRef>(null);

  // ✅ Expose stop function to parent - handles article, quiz, reflection and video
  useImperativeHandle(ref, () => ({
    stopCurrentItem: async () => {
      if (articleRef.current) {
        await articleRef.current.stopItem();
      } else if (quizRef.current) {
        await quizRef.current.stopItem();
      } else if (reflectionRef.current) {
        await reflectionRef.current.stopItem();
      } else if (caseStudyRef.current) {
        await caseStudyRef.current.stopItem();
      } else if (videoRef.current) {
        await videoRef.current.stopItem();
      }
    },
    getCurrentDetails: () => {
      if (quizRef.current?.getCurrentDetails) {
        return quizRef.current.getCurrentDetails();
      }
      return {};
    }
  }));
  const submitFeedback = useSubmitFeedback(item._id.toString())

  const handleFeedbackSubmit = async (formData: any) => {


  };

  const renderContent = () => {
    const itemType = item.type.toLowerCase();
    switch (itemType) {
      case 'video':
        /**
         * One player component for both sources. Video picks an HLS or YouTube
         * backend from `source` internally, so proctoring, seek gating,
         * watch-time and the overlays are shared rather than duplicated —
         * uploaded lessons get the same enforcement as YouTube ones.
         */
        return <Video
          key={item._id.toString()}
          ref={videoRef}
          URL={item.details?.URL ? item.details.URL : ''}
          source={item.details?.source}
          assetId={item.details?.assetId}
          startTime={item.details?.startTime ? item.details.startTime : ''}
          endTime={item.details?.endTime ? item.details.endTime : ''}
          points={item.details?.points ? item.details.points : ''}
          doGesture={doGesture}
          onNext={onNext}
          keyboardLockEnabled={keyboardLockEnabled}
          focusMode={focusMode}
          isProgressUpdating={isProgressUpdating}
          rewindVid={rewindVid || false}
          pauseVid={pauseVid || false}
          pauseSignal={pauseSignal}
          awayPaused={awayPaused}
          readyToDetect={readyToDetect}
          anomalies={anomalies}
          linearProgressionEnabled={linearProgressionEnabled}
          seekForwardEnabled={seekForwardEnabled}
          isCompleted={item.isCompleted || false}
          isAlreadyWatched = {item.isAlreadyWatched || false}
          completedItemIdsRef={completedItemIdsRef}
          nextItemId={nextItem?.itemId?.toString()}
          cohortId={cohortId}
          cohortName={cohortName}
        />;

      case 'quiz':
        return <Quiz
          key={item._id.toString()}
          ref={quizRef}
          questionBankRefs={item.details?.questionBankRefs || []}
          passThreshold={item.details?.passThreshold || 0}
          maxAttempts={item.details?.maxAttempts || 1}
          quizType={item.details?.quizType || ''}
          releaseTime={item.details?.releaseTime}
          questionVisibility={item.details?.questionVisibility || 0}
          deadline={item.details?.deadline}
          approximateTimeToComplete={item.details?.approximateTimeToComplete || ''}
          allowPartialGrading={item.details?.allowPartialGrading || false}
          allowHint={item.details?.allowHint || false}
          allowSkip={item.details?.allowSkip || false}
          showCorrectAnswersAfterSubmission={item.details?.showCorrectAnswersAfterSubmission || false}
          showExplanationAfterSubmission={item.details?.showExplanationAfterSubmission || false}
          showScoreAfterSubmission={item.details?.showScoreAfterSubmission || false}
          quizId={item._id || ''}
          doGesture={doGesture}
          onNext={onNext}
          onPrevVideo={onPrevVideo}
          isProgressUpdating={isProgressUpdating}
          isNavigatingToPrev={isNavigatingToPrev}
          attemptId={attemptId}
          setAttemptId={setAttemptId}
          displayNextLesson={displayNextLesson}
          setQuizPassed={setQuizPassed}
          rewindVid={rewindVid}
          setIsQuizSkipped={setIsQuizSkipped}
          linearProgressionEnabled={linearProgressionEnabled}
          isAlreadyWatched={item.isAlreadyWatched || false}
          completedItemIdsRef={completedItemIdsRef}
          nextItemId={nextItem?.itemId?.toString()}
          pendingStudentQuestionContext={pendingStudentQuestionContext}
          clearPendingStudentQuestionContext={clearPendingStudentQuestionContext}
        />;

      case 'article':
      case 'blog':
        return <Article
          key={item._id.toString()}
          ref={articleRef}
          content={item.details?.content || ''}
          estimatedReadTimeInMinutes={item.details?.estimatedReadTimeInMinutes || ''}
          tags={item.details?.tags || []}
          points={item.details?.points || ''}
          onNext={onNext}
          isProgressUpdating={isProgressUpdating}
          isAlreadyWatched={item.isAlreadyWatched || false}
          completedItemIdsRef={completedItemIdsRef}
        />;

      case 'project':
        return <ProjectItem
          key={item._id.toString()}
          item={{
            _id: item._id,
            name: item.name,
            type: 'PROJECT',
            description: item.details?.description || item.description || ''
          }}
          onSave={() => { }} // Not used in student view
          onCancel={() => { }} // Not used in student view
          isInstructor={false}
          onNext={onNext}
          isProgressUpdating={isProgressUpdating}
        />;
      case 'feedback':
        return <FeedbackForm
          key={item._id.toString()}
          title={item.name}
          description={item.description}
          isOptional={item.isOptional}
          jsonSchema={item?.details?.jsonSchema}
          uiSchema={item?.details?.uiSchema}
          onSubmit={handleFeedbackSubmit}
          isSubmitting={isProgressUpdating}
          onNext={onNext}
          isAlreadyWatched={item.isAlreadyWatched || false}
          completedItemIdsRef={completedItemIdsRef}
          previousItem = {previousItem}
        />;

      case 'reflection':
        return <ReflectionItemPanel
          key={item._id.toString()}
          ref={reflectionRef}
          courseId={courseId}
          courseVersionId={versionId}
          itemId={item._id.toString()}
          title={item.name}
          prompt={item.details?.prompt}
          onNext={onNext}
          isProgressUpdating={isProgressUpdating}
          isAlreadyWatched={item.isAlreadyWatched || false}
          completedItemIdsRef={completedItemIdsRef}
        />;

      case 'case_study':
        return <CaseStudyItemPanel
          key={item._id.toString()}
          ref={caseStudyRef}
          courseId={courseId}
          courseVersionId={versionId}
          itemId={item._id.toString()}
          title={item.name}
          onNext={onNext}
          isProgressUpdating={isProgressUpdating}
          isAlreadyWatched={item.isAlreadyWatched || false}
          completedItemIdsRef={completedItemIdsRef}
        />;

      default:
        return (
          <div className="flex items-center justify-center h-64">
            <p className="text-muted-foreground">Unsupported item type: {item.type}</p>
          </div>
        );
    }
  };

  return (
    <div className={`${item.type.toLowerCase()==="video" ? (focusMode ? "fixed inset-0 z-40 bg-stage h-screen" : "h-[85vh]") : "h-full" } w-full overflow-auto`}>
      {renderContent()}
    </div>
  );
});

ItemContainer.displayName = 'ItemContainer';

export default ItemContainer;